import { useEffect, useState } from 'react'
import { Clock, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface SessionExpiryWarningProps {
  isVisible: boolean
  expiresAt: Date | null
  onExtend: () => Promise<{ success?: boolean; message?: string } | undefined>
  onDismiss: () => void
  onLogout: () => void
}

export function SessionExpiryWarning({
  isVisible,
  expiresAt,
  onExtend,
  onDismiss,
  onLogout
}: SessionExpiryWarningProps) {
  const [isExtending, setIsExtending] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState('')

  useEffect(() => {
    if (!isVisible || !expiresAt) return

    const updateTimer = () => {
      const now = new Date()
      const diff = expiresAt.getTime() - now.getTime()

      if (diff <= 0) {
        setTimeRemaining('expired')
        return
      }

      const minutes = Math.floor(diff / 60000)
      const seconds = Math.floor((diff % 60000) / 1000)

      if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s`)
      } else {
        setTimeRemaining(`${seconds}s`)
      }
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)
    return () => clearInterval(interval)
  }, [isVisible, expiresAt])

  const handleExtend = async () => {
    setIsExtending(true)
    try {
      await onExtend()
    } finally {
      setIsExtending(false)
    }
  }

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-md rounded-2xl border bg-card shadow-lg card-inset overflow-hidden">
        <div className="p-6">
          <div className="mb-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
              <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-0.5">Session</p>
              <h3 className="text-lg font-heading font-semibold tracking-tight">
                Session expiring soon
              </h3>
            </div>
          </div>

          <p className="mb-1 text-sm text-muted-foreground">
            Your session will expire in{' '}
            <span className="font-semibold text-amber-600 dark:text-amber-400 tabular-nums">{timeRemaining}</span>.
          </p>
          <p className="mb-6 text-sm text-muted-foreground">
            Extend it to keep working — any unsaved changes may be lost if it expires.
          </p>

          <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap gap-2">
            <Button
              variant="ghost"
              onClick={onLogout}
              className="text-muted-foreground hover:text-destructive sm:order-1"
            >
              Logout
            </Button>
            <Button
              variant="outline"
              onClick={onDismiss}
              title="Hide this warning — your session will still expire"
              className="sm:order-2"
            >
              Remind me later
            </Button>
            <Button
              onClick={handleExtend}
              disabled={isExtending}
              className="bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 shadow-md shadow-brand-periwinkle/20 sm:flex-1 sm:order-3"
            >
              {isExtending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Extending…
                </>
              ) : (
                'Extend session'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
